/**
 * #1331 Part 3 (PR 2) — keep WhatsApp inbox data fresh. partner-service emits
 * `whatsapp:conversation.updated` { conversationId } to the admin role rooms (which
 * the socket joins on connect); on it, every query under ['wa-inbox'] refetches
 * through the admin API. The event carries no message content, so a duplicate
 * delivery (an admin holding both admin roles) only costs one extra refetch.
 */
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { websocketService } from '@/services/websocket'

export const WA_INBOX_EVENT = 'whatsapp:conversation.updated'
export const WA_INBOX_QUERY_KEY = ['wa-inbox'] as const

/**
 * Polling fallback for the inbox queries. The browser socket has not connected since
 * the move to httpOnly cookies (AuthContext.token is always null and the socket server
 * only reads a handshake token), so the event above never arrives today. Polling keeps
 * the inbox fresh; once the socket connects again, the event makes updates instant.
 */
export const WA_INBOX_POLL_MS = 15_000

export function useWhatsAppInboxLive(enabled = true): void {
  const queryClient = useQueryClient()
  useEffect(() => {
    if (!enabled) return
    const handler = () => {
      void queryClient.invalidateQueries({ queryKey: WA_INBOX_QUERY_KEY })
    }
    websocketService.on(WA_INBOX_EVENT, handler)
    return () => websocketService.off(WA_INBOX_EVENT, handler)
  }, [enabled, queryClient])
}
